import React, { useState, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Upload, AlertCircle, CheckCircle, FileText, Trash2, Loader2, Info,
} from 'lucide-react';

// Accepts: CLIENTE_DD-MM-YYYY_FACTURA_BASE_FINAL_COMERCIAL[_CIF].pdf
// Los importes BASE y FINAL admiten un signo menos opcional (-?): los bonos /
// abonos son notas de credito con importe NEGATIVO (ej. ..._-138,88_-168,04_...)
// y antes el regex los rechazaba como "formato invalido".
const FILENAME_REGEX = /^(.+?)_(\d{2}-\d{2}-\d{4}|\d{8})_(.+?)_(-?\d+[.,]?\d*)_(-?\d+[.,]?\d*)_(.+)\.pdf$/i;

const formatCurrency = (value) => {
  if (value == null) return '—';
  return new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR' }).format(value);
};

const parseFilename = (filename) => {
  const match = filename.match(FILENAME_REGEX);
  if (!match) return null;
  const [, cliente, fecha, factura, base, final_, rest] = match;
  // rest may be "COMERCIAL" or "COMERCIAL_CIF"
  const comercial = rest.split('_')[0];

  // Handle both DD-MM-YYYY and YYYYMMDD date formats
  let fechaFormatted;
  if (fecha.includes('-')) {
    fechaFormatted = fecha.replace(/-/g, '/');
  } else {
    fechaFormatted = `${fecha.slice(6, 8)}/${fecha.slice(4, 6)}/${fecha.slice(0, 4)}`;
  }

  // Strip leading zeros from client ID for display
  const clienteDisplay = cliente.replace(/^0+/, '') || cliente;

  return {
    cliente: clienteDisplay,
    fecha: fechaFormatted,
    factura,
    base: parseFloat(base.replace(',', '.')),
    final: parseFloat(final_.replace(',', '.')),
    comercial,
  };
};

/* ---------- Dropzone ---------- */
const Dropzone = ({ onFiles, disabled }) => {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    if (disabled) return;
    const files = Array.from(e.dataTransfer.files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    if (files.length) onFiles(files.slice(0, 1000));
  }, [onFiles, disabled]);

  const handleDragOver = useCallback((e) => {
    e.preventDefault();
    if (!disabled) setDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback(() => setDragging(false), []);

  const handleClick = () => { if (!disabled) inputRef.current?.click(); };

  const handleChange = (e) => {
    const files = Array.from(e.target.files).slice(0, 1000);
    if (files.length) onFiles(files);
    e.target.value = '';
  };

  return (
    <div
      onClick={handleClick}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
        dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300 hover:border-blue-400 hover:bg-gray-50'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf"
        multiple
        className="hidden"
        onChange={handleChange}
        disabled={disabled}
      />
      <Upload size={32} className="mx-auto text-gray-400 mb-2" />
      <p className="text-gray-600 font-medium text-sm">Arrastre archivos PDF aquí o haga clic para seleccionar</p>
      <p className="text-xs text-gray-400 mt-1">Máximo 1000 archivos · Solo PDF</p>
    </div>
  );
};

/* ========== INVOICE UPLOAD ========== */
const InvoiceUpload = () => {
  const { token, API_URL } = useAuth();
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [parsedFiles, setParsedFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState('');

  const handleFiles = async (files) => {
    setUploadResult(null);
    setError('');
    const parsed = files.map((file) => {
      const data = parseFilename(file.name);
      return { file, name: file.name, parsed: data, valid: !!data, clientInfo: null };
    });

    // Fetch forma_pago lookup table and client info in parallel
    const uniqueClientIds = [...new Set(parsed.filter(p => p.valid).map(p => p.parsed.cliente))];
    const clientInfoMap = {};
    let formaPagoMap = {};

    // Fetch forma_pago descriptions
    const fpPromise = fetch(`${API_URL}/clients/forma-pago`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(r => r.ok ? r.json() : { data: [] }).then(data => {
      const list = data.data || data.formas_pago || data;
      if (Array.isArray(list)) {
        list.forEach(fp => { formaPagoMap[String(fp.id)] = fp.descripcion || fp.nombre || ''; });
      }
    }).catch(() => {});

    // Fetch clients
    const clientPromises = uniqueClientIds.map(async (cid) => {
      try {
        const res = await fetch(`${API_URL}/clients/${cid}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          const cl = data.client || data;
          clientInfoMap[cid] = {
            razon_social: cl.razon_social || '',
            mail_admin: cl.mail_admin || '',
            iban: cl.iban || '',
            forma_pago_id: cl.forma_pago || '',
          };
        }
      } catch { /* ignore */ }
    });

    await Promise.all([fpPromise, ...clientPromises]);

    // Resolve forma_pago names
    for (const cid of Object.keys(clientInfoMap)) {
      const fpId = String(clientInfoMap[cid].forma_pago_id);
      clientInfoMap[cid].forma_pago = formaPagoMap[fpId] || fpId;
    }

    // Attach client info to parsed files
    for (const p of parsed) {
      if (p.valid && clientInfoMap[p.parsed.cliente]) {
        p.clientInfo = clientInfoMap[p.parsed.cliente];
      }
    }

    setSelectedFiles(files);
    setParsedFiles(parsed);
  };

  const removeFile = (index) => {
    const newFiles = [...selectedFiles];
    const newParsed = [...parsedFiles];
    newFiles.splice(index, 1);
    newParsed.splice(index, 1);
    setSelectedFiles(newFiles);
    setParsedFiles(newParsed);
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setParsedFiles([]);
    setUploadResult(null);
    setError('');
  };

  const handleUpload = async () => {
    if (!selectedFiles.length) return;
    setUploading(true);
    setError('');
    setUploadResult(null);

    // Solo subimos los que el front pudo parsear (validos). Los que no, NO se
    // suben (antes se subian igual y el back los procesaba mal / con importe roto).
    const validNames = new Set(parsedFiles.filter((p) => p.valid).map((p) => p.name));
    const formData = new FormData();
    selectedFiles.filter((f) => validNames.has(f.name)).forEach((f) => formData.append('archivos', f));

    // Metadatos ya parseados por el front (fuente de verdad para el back). La
    // fecha de parseFilename viene DD/MM/YYYY -> la mandamos como YYYY-MM-DD.
    const metadata = {};
    parsedFiles.forEach((p) => {
      if (p.valid && p.parsed) {
        const [d, m, y] = String(p.parsed.fecha).split('/');
        metadata[p.name] = {
          cliente: p.parsed.cliente,
          fecha: `${y}-${m}-${d}`,
          factura: p.parsed.factura,
          base: p.parsed.base,
          final: p.parsed.final,
          comercial: p.parsed.comercial,
        };
      }
    });
    formData.append('metadata', JSON.stringify(metadata));

    // Send email overrides (edited by user in preview table)
    const emailOverrides = {};
    parsedFiles.forEach((p) => {
      if (p.valid && p.editedEmail !== undefined) {
        emailOverrides[p.name] = p.editedEmail;
      }
    });
    formData.append('emailOverrides', JSON.stringify(emailOverrides));

    try {
      const res = await fetch(`${API_URL}/commercial/facturas/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.message || 'Error al subir facturas');
      }
      const data = await res.json();
      setUploadResult(data);
      setSelectedFiles([]);
      setParsedFiles([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const invalidCount = parsedFiles.filter(f => !f.valid).length;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 px-6 py-4 border-b border-gray-200 bg-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center">
            <Upload size={20} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-800">Carga de Facturas</h1>
            <p className="text-sm text-gray-500">Suba facturas en formato PDF</p>
          </div>
        </div>
      </div>

      {/* Content: centered upload panel */}
      <div className="flex-1 overflow-y-auto bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto space-y-4">
          {/* Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3 flex gap-3">
            <Info size={18} className="text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-xs font-medium text-blue-800">Formato del nombre de archivo:</p>
              <code className="text-xs text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded mt-1 inline-block">
                {'CLIENTE_DD-MM-YYYY_FACTURA_BASE_FINAL_COMERCIAL_CIF.pdf'}
              </code>
              <p className="text-xs text-blue-600 mt-1">
                Ej: 00040153_24-03-2026_2601570_138,88_168,04_0_b14446306.pdf
              </p>
            </div>
          </div>

          {/* Dropzone */}
          <Dropzone onFiles={handleFiles} disabled={uploading} />

          {/* Selected Files */}
          {parsedFiles.length > 0 && (
            <div className="bg-white rounded-xl shadow border border-gray-200 overflow-hidden">
              <div className="flex items-center justify-between p-3 border-b border-gray-200">
                <div className="flex items-center gap-2">
                  <FileText size={16} className="text-gray-500" />
                  <span className="font-semibold text-gray-700 text-sm">
                    {parsedFiles.length} archivo{parsedFiles.length !== 1 ? 's' : ''}
                  </span>
                  {invalidCount > 0 && (
                    <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded-full">
                      {invalidCount} inválido{invalidCount !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <button onClick={clearFiles} className="text-xs text-gray-400 hover:text-red-500 transition-colors">
                  Limpiar
                </button>
              </div>
              {/* Warning banner if any client missing email */}
              {(() => {
                const missingEmail = parsedFiles.filter(p => p.valid && !(p.editedEmail || p.clientInfo?.mail_admin));
                if (missingEmail.length === 0) return null;
                return (
                  <div className="px-3 py-2 bg-amber-50 border-b border-amber-200 flex items-center gap-2">
                    <AlertCircle size={14} className="text-amber-600 flex-shrink-0" />
                    <span className="text-xs text-amber-800">
                      <strong>{missingEmail.length}</strong> factura(s) con clientes sin email de notificación. La factura se subirá pero no se enviará email automático.
                    </span>
                  </div>
                );
              })()}
              <div className="overflow-auto" style={{ maxHeight: '60vh' }}>
                <table className="w-full text-xs" style={{ minWidth: '1200px' }}>
                  <thead className="sticky top-0 bg-gray-50 z-10">
                    <tr className="text-left text-gray-500 uppercase" style={{ fontSize: '10px' }}>
                      <th className="px-2 py-2">Factura</th>
                      <th className="px-2 py-2">Cod.</th>
                      <th className="px-2 py-2">Razón Social</th>
                      <th className="px-2 py-2">Email</th>
                      <th className="px-2 py-2">IBAN</th>
                      <th className="px-2 py-2">Forma de Pago</th>
                      <th className="px-2 py-2 text-right">Base</th>
                      <th className="px-2 py-2 text-right">Final</th>
                      <th className="px-2 py-2 w-6"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {parsedFiles.map((item, idx) => {
                      const currentEmail = item.editedEmail ?? item.clientInfo?.mail_admin ?? '';
                      const noEmail = item.valid && !currentEmail;
                      const rowClass = !item.valid
                        ? 'bg-red-50'
                        : noEmail
                          ? 'bg-amber-50 hover:bg-amber-100'
                          : 'hover:bg-gray-50';
                      return (
                        <tr key={idx} className={rowClass}>
                          {item.valid ? (
                            <>
                              <td className="px-2 py-1.5 font-medium whitespace-nowrap">{item.parsed.factura}</td>
                              <td className="px-2 py-1.5 text-gray-500 whitespace-nowrap">{item.parsed.cliente}</td>
                              <td className="px-2 py-1.5 max-w-[200px] truncate" title={item.clientInfo?.razon_social || ''}>
                                {item.clientInfo?.razon_social || <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-2 py-1.5">
                                <div className="flex items-center gap-1">
                                  {noEmail && <AlertCircle size={12} className="text-amber-600 flex-shrink-0" />}
                                  <input
                                    type="text"
                                    value={currentEmail}
                                    onChange={(e) => {
                                      const updated = [...parsedFiles];
                                      updated[idx] = { ...updated[idx], editedEmail: e.target.value };
                                      setParsedFiles(updated);
                                    }}
                                    className={`w-full px-1.5 py-1 border rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400 focus:border-blue-400 ${
                                      noEmail
                                        ? 'border-amber-400 bg-amber-50 placeholder-amber-600'
                                        : 'border-gray-200'
                                    }`}
                                    placeholder={noEmail ? '⚠ Sin email — escribir manualmente' : 'email@...'}
                                  />
                                </div>
                              </td>
                              <td className="px-2 py-1.5 text-gray-700 font-mono whitespace-nowrap">
                                {item.clientInfo?.iban || <span className="text-gray-300 font-sans">— Sin IBAN —</span>}
                              </td>
                              <td className="px-2 py-1.5 text-gray-700 whitespace-nowrap">
                                {item.clientInfo?.forma_pago || <span className="text-gray-300">—</span>}
                              </td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap">{formatCurrency(item.parsed.base)}</td>
                              <td className="px-2 py-1.5 text-right whitespace-nowrap font-medium">{formatCurrency(item.parsed.final)}</td>
                            </>
                          ) : (
                            <td colSpan={8} className="px-2 py-1.5 text-red-600">
                              <span className="flex items-center gap-1"><AlertCircle size={12} /> {item.name} — Formato inválido</span>
                            </td>
                          )}
                          <td className="px-2 py-1.5 text-center">
                            <button onClick={() => removeFile(idx)} className="text-gray-400 hover:text-red-500">
                              <Trash2 size={12} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="p-3 border-t border-gray-200 flex justify-end">
                <button
                  onClick={handleUpload}
                  disabled={uploading || !selectedFiles.length}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-500 hover:bg-blue-600 disabled:bg-gray-300 text-white font-medium text-sm rounded-lg transition-colors"
                >
                  {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                  {uploading ? 'Subiendo...' : 'Subir Facturas'}
                </button>
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-xl flex items-center gap-2 text-sm">
              <AlertCircle size={18} />
              <span>{error}</span>
            </div>
          )}

          {/* Upload Results */}
          {uploadResult && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3 space-y-2">
              <div className="flex items-center gap-2 text-green-700 font-semibold text-sm">
                <CheckCircle size={18} />
                Resultado de la carga
              </div>
              {/* Resumen general */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-white rounded-lg p-2 text-center border border-green-200">
                  <div className="text-xl font-bold text-green-600">{uploadResult.uploaded ?? 0}</div>
                  <div className="text-gray-500 text-xs">Subidos</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border border-yellow-200">
                  <div className="text-xl font-bold text-yellow-600">
                    {Array.isArray(uploadResult.skipped) ? uploadResult.skipped.length : (uploadResult.skipped ?? 0)}
                  </div>
                  <div className="text-gray-500 text-xs">Omitidos</div>
                </div>
                <div className="bg-white rounded-lg p-2 text-center border border-red-200">
                  <div className="text-xl font-bold text-red-600">{uploadResult.errors?.length ?? 0}</div>
                  <div className="text-gray-500 text-xs">Errores</div>
                </div>
              </div>

              {/* 3 estados de envío de email */}
              {(uploadResult.emailOk || uploadResult.emailFailed || uploadResult.emailNoRecipient) && (
                <div className="grid grid-cols-3 gap-3 text-sm mt-2">
                  <div className="bg-white rounded-lg p-2 text-center border border-emerald-200">
                    <div className="text-xl font-bold text-emerald-600">{uploadResult.emailOk?.length ?? 0}</div>
                    <div className="text-gray-500 text-xs">Email enviado</div>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center border border-orange-200">
                    <div className="text-xl font-bold text-orange-600">{uploadResult.emailFailed?.length ?? 0}</div>
                    <div className="text-gray-500 text-xs">Email fallido</div>
                  </div>
                  <div className="bg-white rounded-lg p-2 text-center border border-gray-200">
                    <div className="text-xl font-bold text-gray-600">{uploadResult.emailNoRecipient?.length ?? 0}</div>
                    <div className="text-gray-500 text-xs">Sin destinatario</div>
                  </div>
                </div>
              )}

              {Array.isArray(uploadResult.skipped) && uploadResult.skipped.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-yellow-700 mb-1">Facturas omitidas:</div>
                  <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.skipped.map((s, i) => (
                      <div key={i} className="px-2 py-1 rounded bg-yellow-50 border border-yellow-200">
                        <div className="font-medium text-yellow-800">{s.file}</div>
                        <div className="text-yellow-700">{s.reason}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {uploadResult.errors && uploadResult.errors.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-red-700 mb-1">Errores:</div>
                  <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.errors.map((d, i) => (
                      <div key={i} className="px-2 py-1 rounded bg-red-50 border border-red-200">
                        <div className="font-medium text-red-800">{d.file}</div>
                        <div className="text-red-700">{d.error}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Facturas subidas pero SIN destinatario */}
              {Array.isArray(uploadResult.emailNoRecipient) && uploadResult.emailNoRecipient.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-gray-700 mb-1">Subidas SIN destinatario (cliente sin email):</div>
                  <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.emailNoRecipient.map((s, i) => (
                      <div key={i} className="px-2 py-1 rounded bg-gray-50 border border-gray-200">
                        <div className="font-medium text-gray-800">Factura {s.factura}</div>
                        <div className="text-gray-600">Cliente: {s.clientName}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Facturas subidas pero email FALLÓ */}
              {Array.isArray(uploadResult.emailFailed) && uploadResult.emailFailed.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-orange-700 mb-1">Subidas pero email FALLÓ:</div>
                  <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.emailFailed.map((s, i) => (
                      <div key={i} className="px-2 py-1 rounded bg-orange-50 border border-orange-200">
                        <div className="font-medium text-orange-800">Factura {s.factura}</div>
                        <div className="text-orange-700">{s.fallos?.join('; ') || s.error || 'error desconocido'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Facturas subidas + email enviado correctamente */}
              {Array.isArray(uploadResult.emailOk) && uploadResult.emailOk.length > 0 && (
                <div className="mt-2">
                  <div className="text-xs font-semibold text-emerald-700 mb-1">Subidas y email enviado correctamente:</div>
                  <div className="text-xs text-gray-600 space-y-1 max-h-40 overflow-y-auto">
                    {uploadResult.emailOk.map((s, i) => (
                      <div key={i} className="px-2 py-1 rounded bg-emerald-50 border border-emerald-200">
                        <div className="font-medium text-emerald-800">✓ Factura {s.factura}</div>
                        <div className="text-emerald-700">A: {s.destinatarios?.join(', ')}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InvoiceUpload;
